import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Pane,
  Heading,
  Spinner,
  Tab,
  Tablist,
  TabNavigation,
  Badge,
  Text,
  SearchIcon
} from 'evergreen-ui';
import { useProject } from '../../context/ProjectContext';
import { useUser } from '../../context/UserContext';
import { useResultsData } from './hooks/useResultsData';
import { useSearch } from './hooks/useSearch';
import { useEntityOverview } from './hooks/useEntityOverview';
import { SearchBar } from './components/SearchBar';
import { ServicesTab } from './components/ServicesTab';
import { DomainsTab } from './components/DomainsTab';
import { HostsTab } from './components/HostsTab';
import { WebAppsTab } from './components/WebAppsTab';
import { FindingsTab } from './components/FindingsTab';
import EntityOverviewPanel from './EntityOverviewPanel';

const Results = () => {
  const { currentProject } = useProject();
  const { currentUser, fetchCurrentUser } = useUser();
  
  // Fetch current user on mount if not already loaded
  useEffect(() => {
    if (!currentUser) {
      fetchCurrentUser();
    }
  }, [currentUser, fetchCurrentUser]);
  
  const [selectedTab, setSelectedTab] = useState('services');
  const [expandedHosts, setExpandedHosts] = useState({});
  const [expandedDomainGroups, setExpandedDomainGroups] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [manuallyCollapsedComments, setManuallyCollapsedComments] = useState({});
  
  const scrollContainerRef = useRef(null);
  
  // Use custom hooks
  const {
    summary,
    tabData,
    setTabData,
    commentCounts,
    initialLoading,
    actionLoading,
    fetchTabData,
    markAsSeen,
    handleAction,
    isLoadingRef
  } = useResultsData(currentProject);
  
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching
  } = useSearch(currentProject);
  
  const entityOverviewProps = useEntityOverview(currentProject, markAsSeen);

  // Load more data for infinite scroll
  const loadMore = useCallback(() => {
    const currentTabState = tabData[selectedTab];
    if (currentTabState && currentTabState.hasMore && !currentTabState.loading) {
      fetchTabData(selectedTab, currentTabState.offset, true);
    }
  }, [selectedTab, tabData, fetchTabData]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && !isLoadingRef.current) {
      loadMore();
    }
  }, [loadMore, isLoadingRef]);

  // Tab change handler
  const handleTabChange = useCallback((tab) => {
    setSelectedTab(tab);
    if (!tabData[tab].loading) {
      fetchTabData(tab, 0, false);
    }
  }, [tabData, fetchTabData]);

  // Setup scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const toggleHostExpansion = (hostId) => {
    const isExpanding = !expandedHosts[hostId];
    setExpandedHosts(prev => ({
      ...prev,
      [hostId]: !prev[hostId]
    }));
    if (isExpanding) {
      markAsSeen('host', hostId);
    }
  };

  const toggleDomainGroupExpansion = (secondLevelDomain) => {
    setExpandedDomainGroups(prev => ({
      ...prev,
      [secondLevelDomain]: !prev[secondLevelDomain]
    }));
  };

  const toggleCommentsExpansion = (entityId) => {
    const isExpanding = !expandedComments[entityId];
    setExpandedComments(prev => ({
      ...prev,
      [entityId]: !prev[entityId]
    }));

    // When manually expanding, clear the manually collapsed flag
    if (isExpanding) {
      setManuallyCollapsedComments(prev => ({
        ...prev,
        [entityId]: false
      }));

      const parts = entityId.split('_');
      if (parts.length >= 3) {
        const entityType = parts[0];
        const id = parts.slice(2).join('_');
        markAsSeen(entityType, id);
      } else if (parts.length >= 2) {
        const entityType = parts[0];
        const id = parts.slice(1).join('_');
        markAsSeen(entityType, id);
      }
    }
  };

  const collapseComments = (entityId) => {
    setExpandedComments(prev => ({
      ...prev,
      [entityId]: false
    }));
    // Mark as manually collapsed to prevent auto-expansion
    setManuallyCollapsedComments(prev => ({
      ...prev,
      [entityId]: true
    }));
  };

  const renderTabContent = () => {
    const commonProps = {
      searchQuery,
      commentCounts,
      expandedComments,
      manuallyCollapsedComments,
      toggleCommentsExpansion,
      collapseComments,
      actionLoading,
      handleAction,
      openEntityOverview: entityOverviewProps.openEntityOverview,
      currentProject,
      currentUser
    };

    switch (selectedTab) {
      case 'services':
        return (
          <ServicesTab
            {...commonProps}
            services={searchQuery ? searchResults.services : (tabData.services.items || [])}
            isLoading={tabData.services.loading}
            hasMore={tabData.services.hasMore}
          />
        );
      case 'domains':
        return (
          <DomainsTab
            {...commonProps}
            domains={searchQuery ? searchResults.domains : (tabData.domains.items || [])}
            isLoading={tabData.domains.loading}
            hasMore={tabData.domains.hasMore}
            expandedDomainGroups={expandedDomainGroups}
            toggleDomainGroupExpansion={toggleDomainGroupExpansion}
          />
        );
      case 'hosts':
        return (
          <HostsTab
            {...commonProps}
            hosts={searchQuery ? searchResults.hosts : (tabData.hosts.items || [])}
            isLoading={tabData.hosts.loading}
            hasMore={tabData.hosts.hasMore}
            expandedHosts={expandedHosts}
            toggleHostExpansion={toggleHostExpansion}
          />
        );
      case 'webapps':
        return (
          <WebAppsTab
            {...commonProps}
            webapps={searchQuery ? searchResults.webapps : (tabData.webapps.items || [])}
            isLoading={tabData.webapps.loading}
            hasMore={tabData.webapps.hasMore}
            currentProject={currentProject}
          />
        );
      case 'findings':
        return (
          <FindingsTab
            {...commonProps}
            findings={searchQuery ? searchResults.findings : (tabData.findings.items || [])}
            isLoading={tabData.findings.loading}
            hasMore={tabData.findings.hasMore}
          />
        );
      default:
        return null;
    }
  };

  if (!currentProject) {
    return (
      <Pane padding={16}>
        <Text>Please select a project first</Text>
      </Pane>
    );
  }

  if (initialLoading) {
    return (
      <Pane 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        height={400}
      >
        <Spinner />
      </Pane>
    );
  }

  const renderTabWithBadge = (tabKey, label, count, newCount) => (
    <Tab
      isSelected={selectedTab === tabKey}
      onSelect={() => handleTabChange(tabKey)}
      direction="vertical"
      size="medium"
      marginBottom={4}
    >
      <Pane display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Text>{label}</Text>
        <Pane display="flex" gap={4}>
          {searchQuery && searchResults[tabKey]?.length > 0 && (
            <Badge backgroundColor="#1070CA" color="white">
              {searchResults[tabKey].length}
            </Badge>
          )}
          {count > 0 && (
            <Badge backgroundColor="#F7F8FA" color="default">
              {count}
            </Badge>
          )}
          {newCount > 0 && (
            <Badge backgroundColor="#47B881" color="white">
              {newCount}
            </Badge>
          )}
        </Pane>
      </Pane>
    </Tab>
  );

  return (
    <Pane display="flex" height="100vh">
      {/* Sidebar */}
      <Pane
        width={250}
        background="tint1"
        borderRight="1px solid #E6E8F0"
        padding={16}
        display="flex"
        flexDirection="column"
      >
        <Heading size={600} marginBottom={16}>Results</Heading>
        
        <TabNavigation>
          <Tablist flexDirection="column" alignItems="stretch">
            {renderTabWithBadge('services', 'Services', summary.counts.services, summary.new_counts.services)}
            {renderTabWithBadge('domains', 'Domains', summary.counts.domains, summary.new_counts.domains)}
            {renderTabWithBadge('hosts', 'Hosts', summary.counts.hosts, summary.new_counts.hosts)}
            {renderTabWithBadge('webapps', 'Web Apps', summary.counts.webapps, summary.new_counts.webapps)}
            {renderTabWithBadge('findings', 'Findings', summary.counts.findings, summary.new_counts.findings)}
          </Tablist>
        </TabNavigation>
      </Pane>

      {/* Main Content */}
      <Pane flex={1} display="flex" flexDirection="column" height="100vh">
        <SearchBar 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          isSearching={isSearching}
          searchResults={searchResults}
        />

        {/* Tab Content with Scroll */}
        <Pane 
          ref={scrollContainerRef}
          flex={1} 
          padding={24} 
          paddingTop={16}
          overflow="auto"
        >
          {searchQuery && (
            <Pane 
              background="tint1" 
              padding={16} 
              marginBottom={16} 
              borderRadius={6}
              border="1px solid #E6E8F0"
            >
              <Pane display="flex" alignItems="center" justifyContent="space-between">
                <Pane display="flex" alignItems="center" gap={8}>
                  <SearchIcon size={16} color="muted" />
                  <Text size={400} fontWeight="bold">
                    Search Results for "{searchQuery}"
                  </Text>
                  <Badge backgroundColor="#1070CA" color="white">
                    {Object.values(searchResults).reduce((sum, arr) => sum + arr.length, 0)} total results
                  </Badge>
                </Pane>
              </Pane>
            </Pane>
          )}
          
          {renderTabContent()}
        </Pane>
      </Pane>
      
      {/* Entity Overview Side Panel */}
      <EntityOverviewPanel 
        {...entityOverviewProps} 
        markAsSeen={markAsSeen}
        currentProject={currentProject} 
        currentUser={currentUser} 
      />
    </Pane>
  );
};

export default Results;

